<template>
  <div class="max-w-screen-2xl mx-auto relative">
    <div>
      <!-- PageBreadcrumb Navigation -->
      <div class="my-4">
        <div class="flex max-md:flex-col gap-4">
          <PageBreadcrumb class="flex-grow" />

          <Button
            icon="pi pi-refresh"
            @click="refreshAllData"
            :loading="isRefreshing"
            v-if="!(isConfigPage && merchantStore.getMerchants().length > 0)"
            size="small"
            variant="outlined"
            label="Refresh"
            data-test-id="merchant-refresh-btn"
            class="lg:ms-auto"
          />
        </div>
      </div>
      <RouterView
        v-if="isConfigPage && merchantStore.getMerchants().length > 0"
      />

      <DataTable
        v-if="!(isConfigPage && merchantStore.getMerchants().length > 0)"
        id="unified-merchant-tax-table"
        data-test-id="merchant-management-table"
        :value="displayData"
        v-model:filters="filters"
        filterDisplay="row"
        dataKey="MerchantId"
        stripedRows
        removableSort
        lazy
        :paginator="!showSkeletonLoading"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
        :rows="pageSize"
        :rowsPerPageOptions="[10, 20, 50]"
        :totalRecords="totalRecords"
        :first="currentPage * pageSize"
        :sortable="!showSkeletonLoading"
        @page="onPageChange"
        @sort="onSort"
        :sortField="sortField"
        :sortOrder="sortOrder"
        class="shadow-md rounded-lg"
        scrollable
        :scrollHeight="'calc(100vh - 16rem)'"
      >
        <template #empty> No data found.</template>

        <Column
          field="MerchantName"
          header="Merchant"
          filter
          filterField="MerchantName"
          :showFilterMatchModes="false"
          sortable
          style="min-width: 20rem"
          alignFrozen="left"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
          data-test-id="merchant-name-column"
          :pt="{
            bodyCell: [
              'hover:bg-surface-100/40 dark:hover:bg-surface-800/20 hover:shadow z-50 !p-0 group',
            ],
          }"
        >
          <template #body="slotProps">
            <div v-if="showSkeletonLoading" class="p-4">
              <Skeleton width="12rem" height="1rem" />
            </div>
            <RouterLink
              v-else
              :to="{
                name: 'merchantConfiguration',
                params: { id: slotProps.data.MerchantId },
              }"
              class="text-xs text-surface-700 dark:text-surface-200 active:opacity-80 group-hover:text-rch-black dark:group-hover:text-surface-300 inline-block p-4 -my-4 w-full"
              :data-test-id="`merchant-link-${slotProps.data.MerchantId}`"
            >
              <div class="flex items-center gap-1 w-full h-full">
                <div class="flex flex-col">
                  <span class="text-sm font-medium">{{
                    slotProps.data.MerchantName
                  }}</span>
                </div>
                <Transition name="fade">
                  <ArrowTopRightOnSquareIcon
                    class="size-4 opacity-0 group-hover:opacity-100 ml-auto"
                  />
                </Transition>
              </div>
            </RouterLink>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <IconField>
              <InputText
                v-model="filterModel.value"
                type="text"
                @input="
                  () => {
                    filterCallback()
                    debouncedNameSearch(filterModel.value)
                  }
                "
                placeholder="Search by name"
                class="p-column-filter w-full"
                data-test-id="merchant-search-input"
              />
              <InputIcon
                @click="
                  () => {
                    clearFilter('MerchantName', filterCallback)
                    debouncedNameSearch('')
                  }
                "
                class="pi pi-times"
                data-test-id="merchant-search-clear-btn"
              />
            </IconField>
          </template>
        </Column>
        <Column
          field="MerchantId"
          header="Merchant ID"
          filter
          filterField="MerchantId"
          :showFilterMatchModes="false"
          :showFilterOperator="false"
          :showFilterMenu="false"
          sortable
          style="width: 24rem"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
          data-test-id="merchant-id-column"
        >
          <template #body="slotProps">
            <div v-if="showSkeletonLoading">
              <Skeleton width="18rem" height="1rem" />
            </div>
            <TextToClipboard
              v-else
              :text="slotProps.data.MerchantId"
              :max-width="320"
              :show-start-chars="36"
              :show-end-chars="0"
              placeholder="-"
              aria-label="Copy Merchant ID"
              :data-test-merchant-id="slotProps.data.MerchantId"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <IconField>
              <InputText
                v-model="filterModel.value"
                type="text"
                @input="
                  () => {
                    filterCallback()
                    debouncedIdSearch(filterModel.value)
                  }
                "
                placeholder="Search by ID"
                class="p-column-filter w-full"
              />
              <InputIcon
                @click="
                  () => {
                    clearFilter('MerchantId', filterCallback)
                    debouncedIdSearch('')
                  }
                "
                class="pi pi-times"
              />
            </IconField>
          </template>
        </Column>
        <Column
          field="State"
          header="State"
          filter
          filterField="State"
          :showFilterMatchModes="false"
          :showFilterOperator="false"
          :showFilterMenu="false"
          sortable
          style="width: 10rem"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
          data-test-id="merchant-state-column"
        >
          <template #body="slotProps">
            <div v-if="showSkeletonLoading">
              <Skeleton width="5rem" height="1rem" />
            </div>
            <span
              v-else
              class="text-sm text-surface-700 dark:text-surface-300"
              >{{ slotProps.data.State }}</span
            >
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <Select
              v-model="filterModel.value"
              :options="stateOptions"
              placeholder="All states"
              showClear
              class="w-full"
              @change="
                () => handleStateFilterChange(filterModel.value, filterCallback)
              "
            />
          </template>
        </Column>

        <Column
          field="ShopifyStoreCount"
          header="Shopify Stores"
          style="width: 10rem"
          class="text-sm font-medium text-surface-600 dark:text-surface-300 text-center"
          data-test-id="merchant-shopify-stores-column"
        >
          <template #body="slotProps">
            <div v-if="showSkeletonLoading" class="flex justify-center">
              <Skeleton width="2rem" height="1rem" />
            </div>
            <span v-else class="text-sm text-surface-700 dark:text-surface-0">{{
              slotProps.data.ShopifyStoreCount
            }}</span>
          </template>
        </Column>
      </DataTable>
      <AuditInfoDialog
        v-model="auditDialogVisible"
        :subject="auditDialogSubject"
        title="Tax Config Audit Info"
        :label="
          auditDialogSubject
            ? `${auditDialogSubject.MerchantName} (${auditDialogSubject.MerchantId})`
            : ''
        "
        data-test-id="audit-info-dialog"
      />
    </div>

    <!-- Shopify Payouts Drawer (shown when navigating to payouts or pending payouts routes) -->
    <ShopifyPayoutsDrawer
      v-if="isPendingPayoutsRoute"
      :visible="isPendingPayoutsRoute"
      :merchant-id="currentMerchantId"
      @update:visible="handlePayoutsDrawerClose"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, nextTick, watch } from 'vue'
import { useRouter, useRoute, RouterLink } from 'vue-router'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import { useDebounceFn } from '@vueuse/core'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'
import { FilterMatchMode } from '@primevue/core/api'
import { errorToastWithDetails, successToast } from '@/util/toasts.js'
import { normalizeError } from '@/util/errorNormalizer'
import { useMerchantStore } from '@/stores/merchantStore'
import { commonMerchantApi, API_STATES } from '@/services/commonMerchantService'
import type { MerchantQueryParams } from '@/types/commonMerchantGetMerchantData'
import AuditInfoDialog from '@/components/common/AuditInfoDialog.vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import TextToClipboard from '@/components/common/TextToClipboard.vue'
import ShopifyPayoutsDrawer from '@/components/shopifypayouts/ShopifyPayoutsDrawer.vue'
import {
  Button,
  Column,
  DataTable,
  IconField,
  InputIcon,
  InputText,
  Select,
  Skeleton,
} from 'primevue'
import type { DataTableSortEvent } from 'primevue/datatable'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const { authState } = useFrontegg()

const isRefreshing = ref(false)
const showTableLoading = ref(false)
let loadingTimeout: ReturnType<typeof setTimeout> | null = null

// Show loading state only after a delay to avoid flashing for quick operations
function setTableLoading(loading: boolean) {
  if (loading) {
    // Only show loading indicator after 300ms delay
    loadingTimeout = setTimeout(() => {
      showTableLoading.value = true
    }, 300)
  } else {
    // Clear any pending timeout and hide loading immediately
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      loadingTimeout = null
    }
    showTableLoading.value = false
  }
}

// Sorting state
const sortField = ref<string | undefined>('MerchantName')
const sortOrder = ref<number>(1) // 1 = ascending, -1 = descending

// Map server-side field names to client-side field names
const serverToClientFieldMapping: Record<string, string> = {
  Name: 'MerchantName',
  Id: 'MerchantId',
  State: 'State',
}

// Parse sort param from store and sync UI sort state
function syncSortStateFromStore() {
  const storedParams = merchantStore.getCurrentParams()
  if (storedParams.sort) {
    const [field, direction] = storedParams.sort.split(',')
    sortField.value = serverToClientFieldMapping[field] || field
    sortOrder.value = direction === 'desc' ? -1 : 1
  }
}

const filters = ref<Record<string, { value: any; matchMode: string }>>({
  MerchantName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  MerchantId: { value: null, matchMode: FilterMatchMode.CONTAINS },
  State: { value: 'Operating', matchMode: FilterMatchMode.EQUALS },
})

// Current query parameters for server-side filtering
const currentQueryParams = ref<MerchantQueryParams>({
  State: 'Operating',
  page: 0,
  size: 20,
  sort: 'Name,asc',
})

// Pagination state from server
const totalRecords = computed(
  () => merchantStore.getPagedResponse()?.TotalElements ?? 0,
)
const currentPage = computed(
  () => merchantStore.getPagedResponse()?.Number ?? 0,
)
const pageSize = computed(() => merchantStore.getPagedResponse()?.Size ?? 20)

// Stores
const merchantStore = useMerchantStore()

const isLoading = computed(() => {
  return commonMerchantApi.state === API_STATES.LOADING
})

const rows = computed(() => {
  const merchantList = merchantStore.getMerchants()
  if (!merchantList || merchantList.length === 0) return []

  return merchantList.map(m => ({
    MerchantId: m.Id,
    MerchantName: m.Name,
    State: m.State,
    ShopifyStoreCount: Array.isArray(m.ShopifyStores)
      ? m.ShopifyStores.filter(store => store.DeletedAt == null).length
      : 0,
  }))
})

// Determine when to show skeleton loading
const showSkeletonLoading = computed(
  () => isLoading.value || showTableLoading.value,
)

// Create mock data for skeleton loading
function createMockMerchant(index: number) {
  return {
    MerchantId: `skeleton-${index}`,
    MerchantName: '',
    State: '',
    ShopifyStoreCount: 0,
  }
}

// Create skeleton rows when loading
const skeletonRows = computed(() => {
  const length = pageSize.value || 20
  return Array.from({ length }, (_, index) => createMockMerchant(index))
})

// Return skeleton rows when loading, real data otherwise
const displayData = computed(() => {
  if (showSkeletonLoading.value) return skeletonRows.value
  return rows.value
})

// Static state options
const stateOptions = ref(['Operating', 'Dormant'])

const CONFIG_PAGE_ROUTE_NAMES = new Set([
  'merchantConfiguration',
  'merchantConfigurationGeneral',
  'merchantConfigurationShopifyStores',
  'merchantConfigurationShopifyPayouts',
  'merchantConfigurationTaxConfig',
  'merchantConfigurationTransactionSearch',
  'merchantTransactionSearchDetail',
  'merchantConfigurationTransactionDetails',
  'merchantTransactionDetailRecord',
  'merchantConfigurationDropIn',
  'shopifyStoresPayouts',
  'shopifyStoresPayoutDetail',
  'shopifyStoresBalanceTransactionDetail',
])

const isConfigPage = computed(() =>
  CONFIG_PAGE_ROUTE_NAMES.has(route.name as string),
)

const isPendingPayoutsRoute = computed(() => {
  const routeName = route.name as string
  return routeName === 'merchantManagementPayoutsPending'
})

const currentMerchantId = computed(() => {
  const idParam = route.params.id
  return typeof idParam === 'string' ? idParam : ''
})

function handlePayoutsDrawerClose() {
  router.push({ name: 'merchantManagement' })
}

// Debounced search functions for server-side filtering
// Filters work together - searching by ID or Name preserves the current State filter
const debouncedIdSearch = useDebounceFn(async (value: string) => {
  const idValue = value?.trim() || undefined
  setTableLoading(true)
  try {
    await fetchMerchantsWithParams({
      Id: idValue,
      Name: undefined,
    })
  } finally {
    setTableLoading(false)
  }
}, 500)

const debouncedNameSearch = useDebounceFn(async (value: string) => {
  const nameValue = value?.trim() || undefined
  setTableLoading(true)
  try {
    await fetchMerchantsWithParams({
      Name: nameValue,
      Id: undefined,
    })
  } finally {
    setTableLoading(false)
  }
}, 500)

// Handle state filter change with loading state
async function handleStateFilterChange(
  stateValue: string | null,
  filterCallback: () => void,
) {
  filterCallback()
  setTableLoading(true)
  try {
    await fetchMerchantsWithParams({ State: stateValue || undefined })
  } finally {
    setTableLoading(false)
  }
}

// Fetch merchants with partial params (merges with current params)
async function fetchMerchantsWithParams(
  partialParams: Partial<MerchantQueryParams>,
) {
  try {
    const isFilterChange =
      'Id' in partialParams ||
      'Name' in partialParams ||
      'State' in partialParams

    currentQueryParams.value = {
      ...currentQueryParams.value,
      ...partialParams,
      ...(isFilterChange ? { page: 0 } : {}),
    }
    await merchantStore.fetchMerchants(authState, currentQueryParams.value)
  } catch (error) {
    showApiError(error, 'Failed to fetch merchants')
    console.error('Failed to fetch merchants:', error)
  }
}

// Handle pagination change
async function onPageChange(event: { page: number; rows: number }) {
  setTableLoading(true)
  try {
    await fetchMerchantsWithParams({
      page: event.page,
      size: event.rows,
    })
  } finally {
    setTableLoading(false)
  }
}

// Handle sort change - map client field names to server field names
async function onSort(event: DataTableSortEvent) {
  // Update local sort state
  const field =
    typeof event.sortField === 'string' ? event.sortField : undefined
  sortField.value = field
  sortOrder.value = event.sortOrder ?? 1

  setTableLoading(true)
  try {
    // Map client-side field names to server-side field names
    const fieldMapping: Record<string, string> = {
      MerchantName: 'Name',
      MerchantId: 'Id',
      State: 'State',
    }

    const serverField = field ? fieldMapping[field] || field : undefined
    const direction = event.sortOrder === 1 ? 'asc' : 'desc'
    const sortParam = serverField ? `${serverField},${direction}` : undefined

    await fetchMerchantsWithParams({
      sort: sortParam,
      page: 0, // Reset to first page when sorting changes
    })
  } finally {
    setTableLoading(false)
  }
}

onMounted(async () => {
  await ensureData()
})

onUnmounted(() => {
  // Clean up any pending loading timeout
  if (loadingTimeout) {
    clearTimeout(loadingTimeout)
    loadingTimeout = null
  }
})

// When navigating back from a merchant config page to the list,
// re-fetch with default params so the table isn't filtered to a single merchant
watch(isConfigPage, async (configPage, wasConfigPage) => {
  if (!configPage && wasConfigPage) {
    merchantStore.invalidateCache()
    try {
      await merchantStore.fetchMerchants(authState, {
        State: 'Operating',
        sort: 'Name,asc',
      })
    } catch (err) {
      showApiError(err, 'Failed to load merchants')
    }
    // Reset filters to defaults
    filters.value.MerchantName.value = null
    filters.value.MerchantId.value = null
    filters.value.State.value = 'Operating'
    sortField.value = 'MerchantName'
    sortOrder.value = 1
  }
})

// Helper function for API error handling
function showApiError(err: unknown, title: string) {
  const apiError = normalizeError(err)
  errorToastWithDetails(toast, title, apiError.toToastMessage(), apiError)
}

async function ensureData() {
  // Check if we already have valid cached data (navigating back to page)
  // If cache was invalidated (e.g. leaving merchant config), re-fetch with defaults
  const storedParams = merchantStore.getCurrentParams()
  if (merchantStore.getMerchants().length > 0 && merchantStore.isCacheValid(storedParams)) {
    // Sync UI state from stored params
    syncSortStateFromStore()
    syncFilterStateFromStore()
  } else {
    // Fetch merchants with default Operating filter and Name sort
    try {
      merchantStore.invalidateCache()
      await merchantStore.fetchMerchants(authState, {
        State: 'Operating',
        sort: 'Name,asc',
      })
    } catch (err) {
      showApiError(err, 'Failed to load merchants')
    }
  }
}

// Sync filter state from store params
function syncFilterStateFromStore() {
  const storedParams = merchantStore.getCurrentParams()

  // Sync State filter - use stored value or null if State was explicitly cleared
  // Only apply default 'Operating' if no params exist at all (first load)
  if (Object.keys(storedParams).length > 0) {
    filters.value.State.value = storedParams.State || null
  }

  // Sync Name filter
  filters.value.MerchantName.value = storedParams.Name || null

  // Sync Id filter
  filters.value.MerchantId.value = storedParams.Id || null

  // Sync currentQueryParams
  currentQueryParams.value = { ...storedParams }
}

function clearFilter(field: string, callback: Function) {
  filters.value[field].value = null
  nextTick(() => {
    if (callback) callback()
  })
}

// Audit dialog state (kept for AuditInfoDialog component)
const auditDialogVisible = ref(false)
const auditDialogSubject = ref<any | null>(null)

async function refreshAllData() {
  try {
    isRefreshing.value = true
    await merchantStore.fetchMerchants(authState, currentQueryParams.value)
    successToast(
      toast,
      'Refreshed',
      'Merchant data has been refreshed successfully',
    )
  } catch (error: any) {
    showApiError(error, 'Refresh Failed')
  } finally {
    isRefreshing.value = false
  }
}
</script>
